import { Typography } from "@mui/material";
import { ColumnDef } from "@tanstack/react-table";
import { MoreVertical } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { Checkbox } from "../ui/checkbox";

export type FeedbackColumns = {
  id: string;
  type: string;
  message: string;
  date: string;
  images: string[];
  user: string;
  isResolved: boolean;
};

export const columns = (
  onImageClick: (images: string[], index: number) => void,
): ColumnDef<FeedbackColumns>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "type",
    header: () => (
      <div className="text-left">
        <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
          Tipo
        </Typography>
      </div>
    ),
    cell: ({ row: { original: feedback } }) => {
      const type = feedback.type;

      return (
        <div
          className="text-left font-medium max-w-[150px]"
          aria-label="Username"
        >
          <Typography
            fontFamily={"Inter"}
            fontSize={"14px"}
            fontWeight={400}
            className="truncate"
          >
            {type}
          </Typography>
        </div>
      );
    },
  },
  {
    accessorKey: "message",
    header: () => (
      <div className="text-left">
        <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
          Mensaje
        </Typography>
      </div>
    ),
    cell: ({ row: { original: feedback } }) => {
      const message = feedback.message;

      return (
        <Tooltip>
          <TooltipTrigger>
            <div
              className="text-left font-medium max-w-[150px]"
              aria-label="mensaje"
            >
              <Typography
                fontFamily={"Inter"}
                fontSize={"14px"}
                fontWeight={400}
                className="truncate"
              >
                {message}
              </Typography>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={400}>
              {message}
            </Typography>
          </TooltipContent>
        </Tooltip>
      );
    },
    enableHiding: false,
  },
  {
    accessorKey: "date",
    header: () => (
      <div className="text-left">
        <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
          Fecha
        </Typography>
      </div>
    ),
    cell: ({ row: { original: feedback } }) => {
      const date = feedback.date;

      return (
        <div
          className="text-left font-medium max-w-[150px]"
          aria-label="fecha-feedback"
        >
          <Typography
            fontFamily={"Inter"}
            fontSize={"14px"}
            fontWeight={400}
            className="truncate"
          >
            {new Date(date).toLocaleDateString("es-ES", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })}
          </Typography>
        </div>
      );
    },
  },
  {
    accessorKey: "user",
    header: () => (
      <div className="text-left">
        <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
          Usuario
        </Typography>
      </div>
    ),
    cell: ({ row: { original: feedback } }) => {
      const user = feedback.user;

      return (
        <div
          className="text-left font-medium max-w-[150px]"
          aria-label="usuario"
        >
          <Typography
            fontFamily={"Inter"}
            fontSize={"14px"}
            fontWeight={400}
            className="truncate"
          >
            {user}
          </Typography>
        </div>
      );
    },
  },
  {
    id: "actions",
    cell: ({ row: { original: feedback } }) => {
      return (
        <div className="flex items-center gap-2" aria-label="acciones-feedback">
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button
                type="button"
                variant="ghost"
                className="h-8 w-8 p-0 hover:bg-gray-200 rounded-full"
              >
                <span className="sr-only">Abrir menú</span>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="flex flex-col">
              <DropdownMenuLabel>
                <Typography
                  fontFamily={"Inter"}
                  fontSize={"12px"}
                  fontWeight={600}
                >
                  Acciones
                </Typography>
              </DropdownMenuLabel>
              {feedback.images && feedback.images.length > 0 ? (
                feedback.images.map((_, index) => (
                  <DropdownMenuItem
                    key={index}
                    onClick={() => onImageClick(feedback.images, index)}
                  >
                    <Typography fontSize="14px">Imagen {index + 1}</Typography>
                  </DropdownMenuItem>
                ))
              ) : (
                <DropdownMenuItem>
                  <Typography fontSize="14px">No hay imágenes</Typography>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    },
    enableHiding: false,
  },
];
